import {
  getSuffixFromTemplate,
  getViewQueryParamFromTemplate,
} from "@intelligems/ig-plugin/core";
import {
  RedirectDto,
  ShopifyPageType,
  ShopifyTemplateTypeType,
  TemplateAssetType,
  VariationFe,
} from "@intelligems/ig-types";

import { FetchProductsQuery } from "../../../../../generated";
import { FetchCollectionsQuery } from "@src/generated-2026-07";

export const TemplateOptions: {
  value: ShopifyTemplateTypeType;
  text: string;
  previewPath: string;
}[] = [
  { value: "index", text: "Homepage", previewPath: "/" },
  {
    value: "collection",
    text: "Collection",
    previewPath: "/collections/",
  },
  { value: "product", text: "Product", previewPath: "/products/" },
  {
    value: "list-collections",
    text: "Collections List",
    previewPath: "/collections",
  },
  // { value: "article", text: "Article" },
  // { value: "blog", text: "Blog", previewPath: "/blogs/news" },
  // { value: "cart", text: "Cart", previewPath: "/cart" },
  { value: "page", text: "Pages", previewPath: "/pages/" },
  // { value: "404", text: "404", previewPath: "/404" },
  // { value: "password", text: "Password", previewPath: "/password" },
  // { value: "search", text: "Search", previewPath: "/search" },
  // { value: "customers/account", text: "Customer Account" },
  // {
  //   value: "customers/activate_account",
  //   text: "Customer Activate Account",
  // },
  // { value: "customers/addresses", text: "Customer Address" },
  // { value: "customers/login", text: "Customer Login" },
  // { value: "customers/order", text: "Customer Order" },
  // { value: "customers/register", text: "Customer Register" },
  // {
  //   value: "customers/reset_password",
  //   text: "Customer Reset Password",
  // },
];

// this is for custom template tests for the preview path lookup
export const AllTemplateOptions: {
  value: ShopifyTemplateTypeType;
  text: string;
  previewPath: string;
}[] = [
  ...TemplateOptions,
  {
    value: "list-collections",
    text: "Collections List",
    previewPath: "/collections",
  },
  { value: "blog", text: "Blog", previewPath: "/blogs/news" },
  { value: "cart", text: "Cart", previewPath: "/cart" },
  { value: "404", text: "404", previewPath: "/404" },
  { value: "password", text: "Password", previewPath: "/password" },
  { value: "search", text: "Search", previewPath: "/search" },
];

/**
 * Not every `ShopifyTemplateType` has a preview path — `article` has no entry
 * in `AllTemplateOptions`, and stored redirects can still carry template types
 * the picker no longer offers. Returns `undefined` for those rather than
 * asserting the lookup always hits.
 */
export const templateTypeToPreviewPath = (
  templateType: ShopifyTemplateTypeType
): string | undefined => {
  return AllTemplateOptions.find((option) => option.value === templateType)
    ?.previewPath;
};

export const bestMatchingHandle = ({
  redirectState,
  productsData,
  collectionsData,
  shopPages,
  isPersonalization,
  index,
}: {
  redirectState: RedirectDto;
  productsData?: FetchProductsQuery;
  collectionsData?: FetchCollectionsQuery;
  shopPages: ShopifyPageType[];
  isPersonalization: boolean;
  index: number;
}) => {
  if (!redirectState?.queryParams?.[0]?.value) {
    return {
      matchedHandle: undefined,
      refetchCollections: false,
      refetchProducts: false,
    };
  }
  let matchedHandle = "";
  let dataIndex: "products" | "collections" | "pages" | "";
  if (redirectState.templateType === "product") {
    dataIndex = "products";
  } else if (redirectState.templateType === "collection") {
    dataIndex = "collections";
  } else if (redirectState.templateType === "page") {
    dataIndex = "pages";
  } else {
    dataIndex = "";
  }

  const templateValue =
    isPersonalization && index === 0 && redirectState.templateSuffixes
      ? redirectState.templateSuffixes[0]
      : redirectState.queryParams[0]?.value || "";
  const matchedSuffix = getSuffixFromTemplate(templateValue);

  if (dataIndex === "pages") {
    const foundMatchingTemplateIndex = shopPages.findIndex(
      (page) => (page.template_suffix ?? "") === matchedSuffix
    );

    if (foundMatchingTemplateIndex !== -1) {
      matchedHandle = shopPages[foundMatchingTemplateIndex].handle ?? "";
    } else {
      matchedHandle = shopPages[0]?.handle ?? "";
    }
  } else if (dataIndex === "collections") {
    if (!collectionsData) {
      return {
        matchedHandle: undefined,
        refetchCollections: false,
        refetchProducts: false,
      };
    }
    const foundMatchingCollectionTemplateIndex =
      collectionsData.collections.edges.findIndex(
        (node) => (node.node.templateSuffix ?? "") === matchedSuffix
      );
    if (foundMatchingCollectionTemplateIndex !== -1) {
      matchedHandle =
        collectionsData.collections.edges[foundMatchingCollectionTemplateIndex]
          .node.handle ?? "";
    } else {
      return {
        matchedHandle: "",
        refetchCollections: true,
        refetchProducts: false,
      };
    }
  } else if (dataIndex === "products") {
    if (!productsData) {
      return {
        matchedHandle: undefined,
        refetchCollections: false,
        refetchProducts: false,
      };
    }
    const foundMatchingProductTemplateIndex =
      productsData.products.edges.findIndex(
        (node) => (node.node.templateSuffix ?? "") === matchedSuffix
      );

    if (foundMatchingProductTemplateIndex !== -1) {
      matchedHandle =
        productsData.products.edges[foundMatchingProductTemplateIndex].node
          .handle ?? "";
    } else {
      return {
        matchedHandle: "",
        refetchCollections: false,
        refetchProducts: true,
      };
    }
  } else {
    if (!collectionsData) {
      return {
        matchedHandle: undefined,
        refetchCollections: false,
        refetchProducts: false,
      };
    }
    matchedHandle = collectionsData.collections.edges[0]?.node.handle ?? "";
  }

  return {
    matchedHandle,
    refetchCollections: false,
    refetchProducts: false,
  };
};

export const getPreviewUrl = ({
  redirectState,
  matchedHandle,
  index,
  isPersonalization,
  storeUrl,
  screenshot,
}: {
  redirectState: RedirectDto;
  index: number;
  isPersonalization: boolean;
  matchedHandle: string;
  storeUrl?: string;
  screenshot: boolean;
}) => {
  if (!redirectState?.templateType) {
    return "";
  }

  const previewPath = templateTypeToPreviewPath(redirectState.templateType);
  // No preview path for this template type — same "cannot build a preview URL"
  // outcome the guards above and below already return.
  if (previewPath === undefined) {
    return "";
  }

  let basePath = previewPath;

  if (
    redirectState.templateType === "product" ||
    redirectState.templateType === "collection" ||
    redirectState.templateType === "page"
  ) {
    basePath = `${basePath}${matchedHandle}`;
  }
  let templatePath = "";
  if (
    index === 0 &&
    isPersonalization &&
    redirectState.templateSuffixes?.length
  ) {
    templatePath = redirectState.templateSuffixes[index];
  } else if (redirectState.queryParams?.[0]?.value) {
    templatePath = redirectState.queryParams[0].value;
  } else {
    return "";
  }
  return (
    (storeUrl ?? "") +
    basePath +
    `?view=${getViewQueryParamFromTemplate(templatePath)}${
      storeUrl ? "" : screenshot ? "&ig-ss=true&ig-preview-traffic=true" : ""
    }`
  );
};

export const verifyNoTrailingSlash = (inputPath: string) => {
  let path = inputPath;
  if (path.endsWith("/")) {
    path = path.slice(0, -1);
  }
  return path;
};
